/* Site search overlay: a full-width sheet below the header.

   Owns opening and closing, the debounced fetch of live results, keyboard
   navigation and the recent-search list. Results markup comes from
   sections/search-suggest.liquid so prices and translations stay in Liquid. */

const RECENT_KEY = 'act:search:recent';
const RECENT_MAX = 6;

class SearchOverlay extends HTMLElement {
  constructor() {
    super();
    this.sheet = this.querySelector('[data-overlay-sheet]');
    this.veil = this.querySelector('[data-overlay-veil]');
    this.input = this.querySelector('[data-overlay-input]');
    this.body = this.querySelector('[data-overlay-body]');
    this.paneIdle = this.querySelector('[data-overlay-pane="idle"]');
    this.paneResults = this.querySelector('[data-overlay-pane="results"]');
    this.searchForm = this.querySelector('[data-overlay-form]');
    this.allLink = this.querySelector('[data-overlay-all]');
    this.allLabel = this.querySelector('[data-overlay-all-label]');
    this.allLabelPlain = this.allLabel ? this.allLabel.textContent : '';
    this.countLabel = this.querySelector('[data-overlay-count]');
    this.foot = this.querySelector('[data-overlay-foot]');
    this.errorText = this.dataset.errorText || '';
    this.debounce = null;
    this.abort = null;

    this.querySelector('[data-overlay-veil]').addEventListener('click', () => this.close());
    this.querySelector('[data-overlay-close]').addEventListener('click', () => this.close());
    this.input.addEventListener('input', () => {
      clearTimeout(this.debounce);
      clearTimeout(this.settle);
      this.debounce = setTimeout(() => this.runSearch(), 90);
    });
    /* The magnifier is a real submit button now (search-overlay.liquid), so a
       touch tap on a phone fires this instead of relying only on the Enter
       keydown handler below, which needs a real keyboard. Route both through
       submitTerm() so a click and an Enter press remember the term and
       navigate the same way. */
    this.searchForm.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submitTerm();
    });
    /* Bound on the document, not on this element: once trapFocus hands focus
       to the sheet it can still land on the veil or, briefly, outside the
       subtree during a transition, and Escape must keep working regardless.
       Kept as a named, instance-bound method (not an inline arrow function)
       so disconnectedCallback below can remove this exact listener: a
       theme-editor re-render of the header replaces this element without
       ever calling removeEventListener otherwise, leaking one document
       listener, bound to a detached subtree, per re-render. */
    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
    document.addEventListener('keydown', this.handleDocumentKeydown);
    /* Bound once here so open()/close() can add and remove this exact
       reference from the header, same reasoning as the document keydown
       listener above. */
    this.handleHeaderClick = this.handleHeaderClick.bind(this);
    this.addEventListener('click', (event) => {
      const chip = event.target.closest('[data-overlay-recent-term]');
      if (!chip) return;
      this.input.value = chip.dataset.overlayRecentTerm;
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.focus();
    });
    this.addEventListener('click', (event) => {
      const refine = event.target.closest('[data-overlay-refine]');
      if (refine) {
        event.preventDefault();
        this.input.value = refine.dataset.overlayRefine;
        this.runSearch();
        this.input.focus();
        return;
      }
      const hit = event.target.closest('[data-overlay-nav]');
      if (hit) this.rememberTerm(this.input.value);
    });
    this.setPosition();
    /* Same leak as the document keydown listener above: bound on window, so
       it has to be a named method disconnectedCallback can remove. */
    this.handleWindowResize = this.handleWindowResize.bind(this);
    window.addEventListener('resize', this.handleWindowResize);
    this.renderRecent();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleDocumentKeydown);
    window.removeEventListener('resize', this.handleWindowResize);
    /* Same leak this guards against elsewhere in this file: a theme-editor
       re-render can disconnect this element while it is still open, before
       close() ever runs to remove this listener itself. */
    if (this.headerEl) {
      this.headerEl.removeEventListener('click', this.handleHeaderClick);
      this.headerEl = null;
    }
  }

  handleDocumentKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveSelection(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveSelection(-1);
    } else if (event.key === 'Enter') {
      const highlighted = this.navigableIndex >= 0 && this.navigable[this.navigableIndex];
      /* Only take Enter over from whatever it would normally do when it
         belongs to the search field itself, or when arrow-key navigation
         has a highlighted option: otherwise a Tab into a curated-panel
         link or the close button would have its own Enter behaviour
         replaced by submitTerm(), sending the visitor to an empty search
         or blocking the close button entirely. */
      if (event.target !== this.input && !highlighted) return;
      event.preventDefault();
      if (highlighted) {
        this.rememberTerm(this.input.value);
        this.navigable[this.navigableIndex].click();
      } else {
        this.submitTerm();
      }
    }
  }

  handleWindowResize() {
    if (this.isOpen) this.setPosition();
  }

  /* The veil is positioned to start below the header (see setPosition), so
     the header stays clickable while a search is open and a click there
     never reaches the veil, meaning it would not close the overlay on its
     own. Bound directly on .header-group
     while open, this closes the overlay first and then lets the click
     carry on to whatever the header would normally do with it: nothing
     here calls preventDefault() or stopPropagation(), so the cart drawer
     still opens and menu links still navigate. Two exceptions: a click on
     the search trigger is left alone, since the document-level trigger
     listener in this file already toggles the overlay, and closing it
     here first would make that listener see it as closed and reopen it
     right away; and a click inside the overlay itself is left alone too,
     since the overlay's own markup renders inside .header-group and this
     handler is not the veil. */
  handleHeaderClick(event) {
    if (event.target.closest('[data-search-overlay-open]')) return;
    if (event.target.closest('.search-overlay')) return;
    this.close({ restoreFocus: false });
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  get suggestUrl() {
    const base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    const term = encodeURIComponent(this.input.value.trim());
    const section = this.dataset.suggestSection || 'search-suggest';
    // limit_scope=each is load-bearing: without it the limit is shared across
    // the requested types, so asking for 10 of product,collection returns five
    // of each rather than ten products. Ten is Shopify's per-type maximum.
    return `${base}?q=${term}&section_id=${section}&resources[type]=product,collection&resources[limit]=10&resources[limit_scope]=each`;
  }

  async runSearch() {
    const term = this.input.value.trim();
    if (term.length < 2) {
      this.showPane('idle');
      return;
    }
    if (this.abort) this.abort.abort();
    this.abort = new AbortController();
    /* Drop the previous term's total straight away: a stale number beside a
       new term is worse than no number. */
    if (this.allLabel) this.allLabel.textContent = this.allLabelPlain;
    try {
      const response = await fetch(this.suggestUrl, { signal: this.abort.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      this.paneResults.innerHTML = await response.text();
      this.showPane('results');
      const root = this.paneResults.querySelector('.search-suggest');
      /* .search-suggest__grid only renders when there is a hit or a rail to
         show; its absence means the empty-state placeholder is on screen, so
         the see-all link would only point at an empty results page. */
      const hasContent = Boolean(root && root.querySelector('.search-suggest__grid'));
      const searchUrl = (window.routes && window.routes.search_url) || '/search';
      this.allLink.hidden = !hasContent;
      this.allLink.href = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
      /* No count is shown. Predictive search caps at ten per type and reports
         no catalogue total, so any number here would describe the preview
         while reading as the total. Clearing it also wipes a stale error. */
      this.countLabel.textContent = '';
      this.collectNavigable();
      /* Deliberately not awaited: the count is a second, slower request and
         the results are already on screen without it. */
      if (hasContent) {
        this.fetchTotal(term);
        /* A term counts as searched once the visitor stops typing and sees
           results for it, not only when they press Enter or click a hit.
           Someone who types "tissot", looks at what comes back and closes the
           sheet has searched for it, and expects to find it under recent
           searches next time. The wait is long enough that the half-typed
           terms on the way there are not stored; the ones that slip through
           are dropped by rememberTerm, which discards any stored term the new
           one starts with. */
        clearTimeout(this.settle);
        this.settle = setTimeout(() => this.rememberTerm(term), 1200);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.showError();
    }
  }

  /* The see-all link's total. Predictive search reports no catalogue total,
     so sections/search-count.liquid runs the real search and returns just the
     number, already formatted and pluralised in Liquid. Optional by design: if
     it fails, is superseded, or is slow, the link keeps its plain label. */
  async fetchTotal(term) {
    if (!this.allLabel) return;
    const searchUrl = (window.routes && window.routes.search_url) || '/search';
    const url = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last&section_id=search-count`;
    try {
      const response = await fetch(url, { signal: this.abort.signal });
      if (!response.ok) return;
      const markup = await response.text();
      /* The visitor may have typed on while this was in flight, and the label
         belongs to whatever is on screen now, not to the term we asked about. */
      if (this.input.value.trim() !== term) return;
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const label = parsed.querySelector('[data-search-count-label]');
      if (!label) return;
      /* A zero total keeps the plain label. The full search can legitimately
         find nothing the predictive index matched on, and "see all 0 results"
         is a worse invitation than "see all results". */
      if (Number(label.getAttribute('data-search-count')) < 1) return;
      const text = label.getAttribute('data-search-count-label');
      if (text) this.allLabel.textContent = text;
    } catch (error) {
      /* Includes AbortError from a newer keystroke. The plain label stands. */
    }
  }

  showError() {
    this.paneResults.textContent = '';
    const wrap = document.createElement('div');
    wrap.className = 'search-overlay__error';
    wrap.setAttribute('role', 'alert');
    wrap.textContent = this.errorText;
    this.paneResults.appendChild(wrap);
    this.showPane('results');
    this.allLink.hidden = true;
    /* The footer's count slot doubles as the error announcement so a
       failed fetch is not silently invisible. */
    this.countLabel.textContent = this.errorText;
    this.collectNavigable();
  }

  collectNavigable() {
    const pane = this.querySelector('[data-overlay-pane].is-on');
    this.navigable = pane ? Array.from(pane.querySelectorAll('[data-overlay-nav]')) : [];
    /* The list has just been replaced, either by a fresh fetch or a pane
       switch, so any data-on/aria-selected left over from a previous
       navigation is stale, and the input's aria-activedescendant no longer
       points at anything meaningful. */
    this.navigable.forEach((item) => {
      item.removeAttribute('data-on');
      item.setAttribute('aria-selected', 'false');
    });
    this.navigableIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
  }

  moveSelection(step) {
    if (!this.navigable || !this.navigable.length) this.collectNavigable();
    if (!this.navigable.length) return;
    if (this.navigableIndex >= 0) {
      const previous = this.navigable[this.navigableIndex];
      previous.removeAttribute('data-on');
      previous.setAttribute('aria-selected', 'false');
    }
    const size = this.navigable.length;
    /* From "nothing selected" (-1), plain modular arithmetic only lines up
       for step +1 (lands on 0); stepping -1 from -1 would land on size - 2,
       skipping the last item. Treat "nothing selected" as its own case so
       Up from nothing reaches the last item, same as a normal combobox. */
    this.navigableIndex =
      this.navigableIndex < 0 ? (step > 0 ? 0 : size - 1) : (this.navigableIndex + step + size) % size;
    const current = this.navigable[this.navigableIndex];
    current.setAttribute('data-on', '');
    /* aria-activedescendant on the input, not real focus, keeps the caret in
       the search field: moving focus to each item would drop it out of the
       field, so typing more characters after arrowing down would be lost. */
    current.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', current.id);
    const itemBox = current.getBoundingClientRect();
    const bodyBox = this.body.getBoundingClientRect();
    if (itemBox.bottom > bodyBox.bottom - 8) this.body.scrollTop += itemBox.bottom - bodyBox.bottom + 20;
    if (itemBox.top < bodyBox.top + 8) this.body.scrollTop -= bodyBox.top - itemBox.top + 20;
  }

  submitTerm() {
    const term = this.input.value.trim();
    if (!term) {
      /* A click on the submit button moves focus there by default; with
         nothing to search, put focus back on the input so the visitor's
         next keystroke lands where they expect instead of on the button. */
      this.input.focus();
      return;
    }
    this.rememberTerm(term);
    window.location.href = `${(window.routes && window.routes.search_url) || '/search'}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
  }

  setPosition() {
    const header = document.querySelector('.section-header') || document.querySelector('header');
    const top = header ? Math.round(header.getBoundingClientRect().bottom) : 0;
    const offset = Math.max(top, 0);
    this.sheet.style.top = `${offset}px`;
    this.sheet.style.maxHeight = `calc(100vh - ${offset}px)`;
    /* The veil starts where the sheet does, so it never covers the header.
       This overlay sits at z-index 70 and the header at 3 or 4, so a veil
       spanning the whole viewport would sit on top of the header and swallow
       every click on it: the cart, the account link, the menu. Leaving the
       header uncovered keeps all of it usable while a search is open. */
    if (this.veil) this.veil.style.top = `${offset}px`;
  }

  open(opener) {
    this.openedBy = opener || document.activeElement;
    this.setPosition();
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    this.input.setAttribute('aria-expanded', 'true');
    /* Match the pane to whatever the field already holds instead of always
       forcing the idle panel: closing (Escape, the veil, the close button)
       never clears the term, so reopening with a usable term left over
       should show that term's results again, not the curated panel under a
       field that still reads "tissot" with its clear button showing. */
    if (this.input.value.trim().length >= 2) {
      this.runSearch();
    } else {
      this.showPane('idle');
    }
    this.setTriggersExpanded(true);
    this.renderRecent();
    /* Attached only while open, and only ever this one listener at a time:
       open() is not called again on an already-open overlay, so there is
       nothing here to deduplicate. */
    this.headerEl = document.querySelector('.header-group');
    if (this.headerEl) this.headerEl.addEventListener('click', this.handleHeaderClick);
    /* Guarded on isOpen: close() calls removeTrapFocus synchronously, so an
       open() immediately followed by a close() inside this 60ms window would
       otherwise still arm Dawn's focus-trap listeners against a sheet that
       is already hidden again. */
    setTimeout(() => {
      if (this.isOpen) trapFocus(this.sheet, this.input);
    }, 60);
  }

  /* restoreFocus is false when the overlay closes because the visitor clicked
     something else in the header. Focusing the search trigger in that case
     pulls focus off whatever they just clicked, and Dawn's mega menu closes
     itself on focusout (details-disclosure.js), so a click on a top-level
     menu item opened it and shut it again in the same breath. Passing no
     element to removeTrapFocus releases the trap and leaves focus where the
     click put it. */
  close({ restoreFocus = true } = {}) {
    clearTimeout(this.debounce);
    clearTimeout(this.settle);
    if (this.abort) this.abort.abort();
    this.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    this.input.setAttribute('aria-expanded', 'false');
    this.setTriggersExpanded(false);
    /* open() re-runs the search asynchronously when the field still holds a
       usable term, so between close() and that fetch resolving, a
       previously highlighted row would otherwise still carry data-on,
       aria-selected and the input's aria-activedescendant, and Enter would
       activate that stale row instead of submitting the term. collectNavigable()
       already clears exactly this state (and resets navigableIndex to -1);
       it only touches whichever pane currently has is-on, so it is safe
       regardless of which pane was showing, and it does not touch focus or
       aria-expanded, both handled separately above and by removeTrapFocus
       below. */
    this.collectNavigable();
    removeTrapFocus(restoreFocus ? this.openedBy : null);
    this.openedBy = null;
    if (this.headerEl) {
      this.headerEl.removeEventListener('click', this.handleHeaderClick);
      this.headerEl = null;
    }
  }

  setTriggersExpanded(expanded) {
    document.querySelectorAll('[data-search-overlay-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  showPane(name) {
    this.paneIdle.classList.toggle('is-on', name === 'idle');
    this.paneResults.classList.toggle('is-on', name === 'results');
    /* The whole bar goes with the pane, not just the link inside it: on the
       curated idle panel there is nothing to see all of, so an empty bar reads
       as a stray strip of colour under the columns. */
    if (this.foot) this.foot.hidden = name !== 'results';
    if (name === 'idle') {
      this.allLink.hidden = true;
      this.countLabel.textContent = '';
      if (this.allLabel) this.allLabel.textContent = this.allLabelPlain;
    }
    this.collectNavigable();
  }

  readRecent() {
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter((term) => typeof term === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  rememberTerm(term) {
    const clean = (term || '').trim();
    if (clean.length < 2) return;
    const lower = clean.toLowerCase();
    /* Drop any stored term this one starts with, not just an exact repeat: a
       pause part-way through typing can store "tiss", and once "tissot" lands
       the stub is the same search, half finished, and should not take up a
       second chip. */
    const kept = this.readRecent().filter((existing) => {
      const other = existing.toLowerCase();
      return other !== lower && !lower.startsWith(other);
    });
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify([clean, ...kept].slice(0, RECENT_MAX)));
    } catch (error) {
      /* storage unavailable, e.g. private browsing: recent searches are optional */
    }
    this.renderRecent();
  }

  renderRecent() {
    const wrapper = this.querySelector('[data-overlay-recent]');
    const list = this.querySelector('[data-overlay-recent-list]');
    if (!wrapper || !list) return;
    const terms = this.readRecent();
    wrapper.hidden = terms.length === 0;
    /* Recent chips sit inside the same listbox as the idle links, rail
       buttons and empty-state chips, and those are already arrow-navigable,
       so recent chips get the same treatment: role="option", a unique id
       from the same id-prefix scheme, and data-overlay-nav so
       collectNavigable() picks them up. */
    list.innerHTML = terms
      .map((term, index) => {
        const safe = term.replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`);
        return `<button type="button" class="search-overlay__chip" data-overlay-recent-term="${safe}" data-overlay-nav role="option" aria-selected="false" id="SearchOverlayOption-recent-${index}">${safe}</button>`;
      })
      .join('');
    this.collectNavigable();
  }
}

customElements.define('search-overlay', SearchOverlay);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-search-overlay-open]');
  if (!trigger) return;
  event.preventDefault();
  const overlay = document.querySelector('search-overlay');
  if (!overlay) return;
  overlay.isOpen ? overlay.close() : overlay.open(trigger);
});

document.addEventListener('keydown', (event) => {
  const overlay = document.querySelector('search-overlay');
  if (!overlay || overlay.isOpen) return;
  /* Some synthetic-event and accessibility tooling dispatches a plain Event
     rather than a real KeyboardEvent, where .key is undefined; guard before
     calling string methods on it. */
  if (typeof event.key !== 'string') return;
  const typingElsewhere = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement.tagName) || document.activeElement.isContentEditable;
  if (typingElsewhere) return;
  const noModifiers = !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey;
  const isSlash = event.key === '/' && noModifiers;
  const isCmdK = event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey);
  if (isSlash || isCmdK) {
    event.preventDefault();
    overlay.open();
  }
});
